/**
 * Cookie utility functions for client-side cookie management
 */

/**
 * Gets a cookie value by name
 * @param name - The name of the cookie to retrieve
 * @returns The cookie value or undefined if not found
 */
export const getCookie = (name: string): string | undefined => {
	const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))
	return cookie?.split('=')[1]
}

/**
 * Get the appropriate cookie domain based on environment
 * @returns {string} The cookie domain for the current environment
 */
const getCookieDomain = (): string => {
	return import.meta.env.APP_ENV === 'local-dev'
		? '.dev.quintoandar.com.br'
		: import.meta.env.VUE_APP_COOKIE_DOMAIN || '.quintoandar.com.br'
}

/**
 * Clears all cookies by setting their expiration to the past
 */
export const clearAllCookies = (): void => {
	const cookieDomain = getCookieDomain()

	document.cookie.split(';').forEach((cookie) => {
		const eqPos = cookie.indexOf('=')
		const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()

		if (name) {
			document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${cookieDomain}`
		}
	})
}

/**
 * Clears a specific cookie by name
 * @param name - The name of the cookie to clear
 */
export const clearCookie = (name: string): void => {
	const cookieDomain = getCookieDomain()
	document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${cookieDomain}`
}

/**
 * Gets the authentication token from the 5AJWT_AUTH cookie
 * @returns The auth token or undefined if not found
 */
export const getAuthToken = (): string | undefined => {
	return getCookie('5AJWT_AUTH')
}

/**
 * Sets a cookie with the given name and value
 * @param name - The name of the cookie to set
 * @param value - The value of the cookie to set
 */
export const setCookie = (name: string, value: string): void => {
	document.cookie = `${name}=${value}; path=/; domain=${getCookieDomain()}; max-age=3600`
}
